export type AdaptiveQualityTier = 0 | 1 | 2 | 3 | 4

export interface AdaptiveTierConfig {
  renderScale: number
  bloomScale: number
  detailFraction: number
  shadows: boolean
}

export interface AdaptiveQualityState extends AdaptiveTierConfig {
  tier: AdaptiveQualityTier
  targetFps: number
  displayRefreshHz: number
  reason: 'initial' | 'display-calibrated' | 'frame-budget-missed' | 'headroom-recovered'
}

export const ADAPTIVE_QUALITY_TIERS: readonly AdaptiveTierConfig[] = [
  { renderScale: 0.35, bloomScale: 0, detailFraction: 0.34, shadows: false },
  { renderScale: 0.7, bloomScale: 0, detailFraction: 0.5, shadows: true },
  { renderScale: 0.82, bloomScale: 0.45, detailFraction: 0.67, shadows: true },
  { renderScale: 0.92, bloomScale: 0.72, detailFraction: 0.84, shadows: true },
  { renderScale: 1, bloomScale: 1, detailFraction: 1, shadows: true },
] as const

const COMMON_REFRESH_RATES = [30, 40, 50, 60, 75, 90, 100, 120, 144, 165, 240] as const

function clampTier(value: number): AdaptiveQualityTier {
  return Math.max(0, Math.min(4, Math.round(value))) as AdaptiveQualityTier
}

function nearestRefreshRate(rawHz: number): number {
  let nearest: number = COMMON_REFRESH_RATES[0]
  let distance = Math.abs(rawHz - nearest)
  for (const rate of COMMON_REFRESH_RATES) {
    const nextDistance = Math.abs(rawHz - rate)
    if (nextDistance < distance) {
      nearest = rate
      distance = nextDistance
    }
  }
  return nearest
}

/** Estimate the panel cadence from animation-frame deltas and snap it to a common refresh rate. */
export function estimateDisplayRefreshHz(frameDeltasMs: readonly number[]): number {
  const valid = frameDeltasMs.filter((ms) => Number.isFinite(ms) && ms >= 3 && ms <= 40).sort((a, b) => a - b)
  if (!valid.length) return 60
  const trim = Math.floor(valid.length * 0.15)
  const stable = valid.slice(trim, Math.max(trim + 1, valid.length - trim))
  const middle = Math.floor(stable.length / 2)
  const median = stable.length % 2 === 0 ? (stable[middle - 1] + stable[middle]) / 2 : stable[middle]
  return nearestRefreshRate(1000 / median)
}

/** Measure refresh before the render loop starts so GPU load is not mistaken for panel cadence. */
export function probeDisplayRefreshHz(
  sampleCount = 30,
  requestFrame: (callback: FrameRequestCallback) => number = requestAnimationFrame,
): Promise<number> {
  return new Promise((resolve) => {
    const deltas: number[] = []
    let previous = 0
    const tick = (now: number) => {
      if (previous > 0) deltas.push(now - previous)
      previous = now
      if (deltas.length >= Math.max(8, sampleCount)) {
        resolve(estimateDisplayRefreshHz(deltas))
      } else {
        requestFrame(tick)
      }
    }
    requestFrame(tick)
  })
}

export interface AdaptiveQualityOptions {
  targetFps?: number
  displayRefreshHz?: number
  initialTier?: AdaptiveQualityTier
  warmupSeconds?: number
  decisionSeconds?: number
  cooldownSeconds?: number
  recoveryWindows?: number
}

/**
 * Finds the highest tier that can hold the display cadence, capped at 144 FPS.
 * It reacts quickly to missed budgets and recovers slowly to avoid quality oscillation.
 */
export class AdaptiveQualityController {
  private tier: AdaptiveQualityTier
  private readonly requestedTargetFps: number
  private displayRefreshHz: number
  private reason: AdaptiveQualityState['reason'] = 'initial'
  private warmupRemaining: number
  private cooldownRemaining = 0
  private windowSeconds = 0
  private frameSeconds = 0
  private frameCount = 0
  private missedFrames = 0
  private goodWindows = 0
  private readonly decisionSeconds: number
  private readonly cooldownSeconds: number
  private readonly recoveryWindows: number

  constructor(options: AdaptiveQualityOptions = {}) {
    this.tier = clampTier(options.initialTier ?? 4)
    this.requestedTargetFps = Math.max(30, options.targetFps ?? 144)
    this.displayRefreshHz = nearestRefreshRate(Math.max(30, options.displayRefreshHz ?? 144))
    this.warmupRemaining = Math.max(0, options.warmupSeconds ?? 0.75)
    this.decisionSeconds = Math.max(0.1, options.decisionSeconds ?? 0.75)
    this.cooldownSeconds = Math.max(0, options.cooldownSeconds ?? 1.25)
    this.recoveryWindows = Math.max(1, Math.round(options.recoveryWindows ?? 3))
  }

  get state(): AdaptiveQualityState {
    const config = ADAPTIVE_QUALITY_TIERS[this.tier]
    return {
      tier: this.tier,
      targetFps: Math.min(this.requestedTargetFps, this.displayRefreshHz),
      displayRefreshHz: this.displayRefreshHz,
      reason: this.reason,
      ...config,
    }
  }

  setDisplayRefreshHz(refreshHz: number): AdaptiveQualityState {
    if (Number.isFinite(refreshHz) && refreshHz > 0) this.displayRefreshHz = nearestRefreshRate(refreshHz)
    this.reason = 'display-calibrated'
    this.resetWindow()
    return this.state
  }

  sample(dtSeconds: number): AdaptiveQualityState | null {
    if (!(dtSeconds > 0) || dtSeconds >= 0.1) return null
    if (this.warmupRemaining > 0) {
      this.warmupRemaining = Math.max(0, this.warmupRemaining - dtSeconds)
      return null
    }
    this.cooldownRemaining = Math.max(0, this.cooldownRemaining - dtSeconds)

    const budgetSeconds = 1 / this.state.targetFps
    this.windowSeconds += dtSeconds
    this.frameSeconds += dtSeconds
    this.frameCount++
    if (dtSeconds > budgetSeconds * 1.18) this.missedFrames++
    if (this.windowSeconds < this.decisionSeconds) return null

    const averageSeconds = this.frameSeconds / Math.max(1, this.frameCount)
    const missedRatio = this.missedFrames / Math.max(1, this.frameCount)
    this.resetWindow()
    if (this.cooldownRemaining > 0) return null

    if ((averageSeconds > budgetSeconds * 1.12 || missedRatio > 0.12) && this.tier > 0) {
      this.tier = clampTier(this.tier - 1)
      this.reason = 'frame-budget-missed'
      this.goodWindows = 0
      this.cooldownRemaining = this.cooldownSeconds
      return this.state
    }

    const stableAtTarget = averageSeconds <= budgetSeconds * 1.06 && missedRatio <= 0.04
    this.goodWindows = stableAtTarget ? this.goodWindows + 1 : 0
    if (this.goodWindows >= this.recoveryWindows && this.tier < 4) {
      this.tier = clampTier(this.tier + 1)
      this.reason = 'headroom-recovered'
      this.goodWindows = 0
      this.cooldownRemaining = this.cooldownSeconds
      return this.state
    }
    return null
  }

  private resetWindow(): void {
    this.windowSeconds = 0
    this.frameSeconds = 0
    this.frameCount = 0
    this.missedFrames = 0
  }
}
